import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useSelector } from 'react-redux';
import { userSelector } from '../user/userSlice';

import { toast } from 'react-toastify';
import api from '../../services/api';
import apiErrorFormatter from '../../services/apiErrorFormatter';

import ManageLayoutItem from './ManageLayoutItem';
import DialogAddLayoutItem from './DialogAddLayoutItem';

// import { useTheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

function ManageLayout() {
  const { t } = useTranslation('maintenance');

  const { user } = useSelector(userSelector);
  const plantId = user?.plant?.id > 0 ? user?.plant?.id : 0;

  //   const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [showContent, setShowContent] = useState(false);

  const [plants, setPlants] = useState([]);
  const [areas, setAreas] = useState([]);
  const [lines, setLines] = useState([]);
  const [equipments, setEquipments] = useState([]);

  const [selectedPlantId, setSelectedPlantId] = useState('');
  const [selectedAreaId, setSelectedAreaId] = useState('');
  const [selectedLineId, setSelectedLineId] = useState('');
  const [selectedEquipmentId, setSelectedEquipmentId] = useState('');

  const [isOpenDialogAddLayoutItem, setIsOpenDialogAddLayoutItem] =
    useState(false);
  const [dialogAddLayoutItemType, setDialogAddLayoutItemType] = useState('');
  const [dialogAddLayoutItemId, setDialogAddLayoutItemId] = useState(0);

  const [expandedAccordion, setExpandedAccordion] = useState('plant');

  const availableAreas =
    selectedPlantId > 0
      ? areas.filter((area) => area?.plantId === selectedPlantId)
      : areas;
  const availableLines =
    selectedAreaId > 0
      ? lines.filter((line) => line?.areaId === selectedAreaId)
      : lines.filter((line) =>
          availableAreas.some((area) => area?.id === line?.areaId)
        );
  const availableEquipments =
    selectedLineId > 0
      ? equipments.filter((equipment) => equipment?.lineId === selectedLineId)
      : equipments.filter((equipment) =>
          availableLines.some((line) => line?.id === equipment?.lineId)
        );

  useEffect(() => {
    getData();
  }, []);

  const getData = async () => {
    setLoading(true);
    setErrors([]);
    setShowContent(false);
    setPlants([]);
    setAreas([]);
    setLines([]);
    setEquipments([]);
    setSelectedPlantId('');
    setSelectedAreaId('');
    setSelectedLineId('');
    setSelectedEquipmentId('');
    setIsOpenDialogAddLayoutItem(false);
    setDialogAddLayoutItemType('');
    setDialogAddLayoutItemId(0);
    setExpandedAccordion('plant');
    let errors = [];
    await api.get('/maintenance/plants').then(
      (res) => {
        setPlants(res?.data);
      },
      (err) => {
        errors = apiErrorFormatter(err);
      }
    );
    await api.get('/maintenance/areas').then(
      (res) => {
        setAreas(res?.data);
      },
      (err) => {
        errors = errors.concat(
          apiErrorFormatter(err).filter((item) => errors.indexOf(item) < 0)
        );
      }
    );
    await api.get('/maintenance/lines').then(
      (res) => {
        setLines(res?.data);
      },
      (err) => {
        errors = errors.concat(
          apiErrorFormatter(err).filter((item) => errors.indexOf(item) < 0)
        );
      }
    );
    await api.get('/maintenance/equipments').then(
      (res) => {
        setEquipments(res?.data);
      },
      (err) => {
        errors = errors.concat(
          apiErrorFormatter(err).filter((item) => errors.indexOf(item) < 0)
        );
      }
    );
    if (errors.length === 0) setShowContent(true);
    else setErrors(apiErrorFormatter(errors));
    setLoading(false);
  };

  const changeEnabled = async (type, id, enabled) => {
    setLoading(true);
    await api
      .patch('/maintenance/change-enabled', {
        type,
        id,
        enabled,
      })
      .then((res) => {
        if (res.status === 200) {
          if (type === 'plant') {
            // const newPlants = [
            //   ...plants,
            //   {
            //     id: res?.data,
            //     productNumber: values2?.productNumber,
            //     productName: values2?.productName,
            //   },
            // ].sort((a, b) => (a?.productNumber > b?.productNumber ? 1 : -1));

            const newPlants = plants.map((plant) => {
              if (plant?.id === id) {
                return {
                  ...plant,
                  enabled: enabled,
                };
              } else {
                return plant;
              }
            });
            setPlants(newPlants);
            toast.success(t('common:changed_successfully'));
          }
          if (type === 'area') {
            const newAreas = areas.map((area) => {
              if (area?.id === id) {
                return {
                  ...area,
                  enabled: enabled,
                };
              } else {
                return area;
              }
            });
            setAreas(newAreas);
            toast.success(t('common:changed_successfully'));
          }
          if (type === 'line') {
            const newLines = lines.map((line) => {
              if (line?.id === id) {
                return {
                  ...line,
                  enabled: enabled,
                };
              } else {
                return line;
              }
            });
            setLines(newLines);
            toast.success(t('common:changed_successfully'));
          }
          if (type === 'equipment') {
            const newEquipments = equipments.map((equipment) => {
              if (equipment?.id === id) {
                return {
                  ...equipment,
                  enabled: enabled,
                };
              } else {
                return equipment;
              }
            });
            setEquipments(newEquipments);
            toast.success(t('common:changed_successfully'));
          }
        }
      })
      .catch((err) => {
        toast.error(t(apiErrorFormatter(err)));
      });
    setLoading(false);
  };

  const handleChangeAccordion =
    (newExpandedAccordion) => (event, isExpanded) => {
      if (isExpanded && newExpandedAccordion !== expandedAccordion) {
        setExpandedAccordion(newExpandedAccordion);
      }
    };

  const handleChangeSelectedPlant = (id) => {
    const newAvailableAreas =
      id > 0 ? areas.filter((area) => area?.plantId === id) : areas;
    const newAvailableLines = lines.filter((line) =>
      newAvailableAreas.some((area) => area?.id === line?.areaId)
    );
    const newAvailableEquipments = equipments.filter((equipment) =>
      newAvailableLines.some((line) => line?.id === equipment?.lineId)
    );
    if (
      selectedEquipmentId > 0 &&
      !newAvailableEquipments.some(
        (equipment) => equipment?.id === selectedEquipmentId
      )
    ) {
      setSelectedEquipmentId('');
    }
    if (
      selectedLineId > 0 &&
      !newAvailableLines.some((line) => line?.id === selectedLineId)
    ) {
      setSelectedLineId('');
    }
    if (
      selectedAreaId > 0 &&
      !newAvailableAreas.some((area) => area?.id === selectedAreaId)
    ) {
      setSelectedAreaId('');
    }
    setExpandedAccordion('area');
    setSelectedPlantId(id);
  };

  const handleChangeSelectedArea = (id) => {
    const newAvailableLines =
      id > 0 ? lines.filter((line) => line?.areaId === id) : lines;
    const newAvailableEquipments = equipments.filter((equipment) =>
      newAvailableLines.some((line) => line?.id === equipment?.lineId)
    );
    if (
      selectedEquipmentId > 0 &&
      !newAvailableEquipments.some(
        (equipment) => equipment?.id === selectedEquipmentId
      )
    ) {
      setSelectedEquipmentId('');
    }
    if (
      selectedLineId > 0 &&
      !newAvailableLines.some((line) => line?.id === selectedLineId)
    ) {
      setSelectedLineId('');
    }
    setExpandedAccordion('line');
    setSelectedAreaId(id);
  };

  const handleChangeSelectedEquipment = (id) => {};

  const handleChangeSelectedLine = (id) => {
    const newAvailableEquipments =
      id > 0
        ? equipments.filter((equipment) => equipment?.lineId === id)
        : equipments;
    if (
      selectedEquipmentId > 0 &&
      !newAvailableEquipments.some(
        (equipment) => equipment?.id === selectedEquipmentId
      )
    ) {
      setSelectedEquipmentId('');
    }
    setExpandedAccordion('equipment');
    setSelectedLineId(id);
  };

  const handleOpenDialogAddLayoutItem = (type) => {
    if (type === 'plant') {
      setDialogAddLayoutItemType(type);
      setDialogAddLayoutItemId(0);
      setIsOpenDialogAddLayoutItem(true);
    }
    if (type === 'area' && selectedPlantId > 0) {
      setDialogAddLayoutItemType(type);
      setDialogAddLayoutItemId(selectedPlantId);
      setIsOpenDialogAddLayoutItem(true);
    }
    if (type === 'line' && selectedAreaId > 0) {
      setDialogAddLayoutItemType(type);
      setDialogAddLayoutItemId(selectedAreaId);
      setIsOpenDialogAddLayoutItem(true);
    }
    if (type === 'equipment' && selectedLineId > 0) {
      setDialogAddLayoutItemType(type);
      setDialogAddLayoutItemId(selectedLineId);
      setIsOpenDialogAddLayoutItem(true);
    }
  };

  const handleCloseDialogAddLayoutItem = (event, reason) => {
    if (reason && reason === 'backdropClick') return;
    setIsOpenDialogAddLayoutItem(false);
    setDialogAddLayoutItemType('');
    setDialogAddLayoutItemId(0);
  };

  const handleValidateDialogAddLayoutItem = async (type) => {
    setIsOpenDialogAddLayoutItem(false);
    setDialogAddLayoutItemType('');
    setDialogAddLayoutItemId(0);
    if (type === 'plant') {
      setLoading(true);
      await api.get('/maintenance/plants').then(
        (res) => {
          setPlants(res?.data);
        },
        (err) => {
          errors = apiErrorFormatter(err);
        }
      );
      setLoading(false);
    }
    if (type === 'area') {
      setLoading(true);
      await api.get('/maintenance/areas').then(
        (res) => {
          setAreas(res?.data);
        },
        (err) => {
          errors = apiErrorFormatter(err);
        }
      );
      setLoading(false);
    }
    if (type === 'line') {
      setLoading(true);
      await api.get('/maintenance/lines').then(
        (res) => {
          setLines(res?.data);
        },
        (err) => {
          errors = apiErrorFormatter(err);
        }
      );
      setLoading(false);
    }
    if (type === 'equipment') {
      setLoading(true);
      await api.get('/maintenance/equipments').then(
        (res) => {
          setEquipments(res?.data);
        },
        (err) => {
          errors = apiErrorFormatter(err);
        }
      );
      setLoading(false);
    }
  };

  return (
    <Container sx={{ mt: 1, mb: 10 }} maxWidth={false}>
      <Box mb={1}>
        <Divider>{t('manage_layout')}</Divider>
      </Box>
      {!showContent && loading ? (
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={'60px'} />
        </Box>
      ) : null}
      {!showContent && errors.length > 0 ? (
        <Box>
          {errors
            .slice()
            .reverse()
            .map((error) => (
              <Box key={error} mt={1}>
                <Alert variant="outlined" severity="error">
                  {t(error)}
                </Alert>
              </Box>
            ))}
        </Box>
      ) : null}
      {showContent ? (
        <Container maxWidth="md">
          <Paper
            sx={{ mb: 2, pt: 1, pb: 1, pl: 1, pr: 1, textAlign: 'center' }}
            variant="outlined"
          >
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  color="primary"
                  variant="contained"
                  sx={{ py: 1 }}
                  // fullWidth
                  disabled={loading}
                  startIcon={
                    !loading ? (
                      <RestartAltIcon />
                    ) : (
                      <CircularProgress color="inherit" size={20} />
                    )
                  }
                  onClick={getData}
                >
                  {t('common:reload')}
                </Button>
              </Box>
            </Box>
          </Paper>
          <Paper
            sx={{ pt: 1, pb: 1, pl: 1, pr: 1, textAlign: 'center' }}
            variant="outlined"
          >
            <Accordion
              expanded={expandedAccordion === 'plant'}
              onChange={handleChangeAccordion('plant')}
              disabled={loading}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel-plant-content"
                id="panel-plant-header"
              >
                {selectedPlantId > 0 ? (
                  <>
                    <Typography
                      sx={{ width: '150px', flexShrink: 0, textAlign: 'left' }}
                    >
                      {t('plant')}
                    </Typography>
                    <Typography
                      sx={{
                        textAlign: 'left',
                        display: '-webkit-box',
                        WebkitLineClamp: '1',
                        lineClamp: '1',
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                      }}
                    >
                      {
                        plants?.find((x) => x?.id === selectedPlantId)
                          ?.plantName
                      }
                    </Typography>
                  </>
                ) : (
                  <Typography>{t('plant')}</Typography>
                )}
              </AccordionSummary>
              <AccordionDetails>
                <ManageLayoutItem
                  loading={loading}
                  disabled={false}
                  data={plants}
                  dataType="plant"
                  addLayoutItem={handleOpenDialogAddLayoutItem}
                  selectedData={selectedPlantId}
                  handleChangeData={handleChangeSelectedPlant}
                  changeEnabled={changeEnabled}
                />
              </AccordionDetails>
            </Accordion>
            <Accordion
              expanded={expandedAccordion === 'area'}
              onChange={handleChangeAccordion('area')}
              disabled={loading || (selectedPlantId > 0 ? false : true)}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel-area-content"
                id="panel-area-header"
              >
                {selectedAreaId > 0 ? (
                  <>
                    <Typography
                      sx={{ width: '150px', flexShrink: 0, textAlign: 'left' }}
                    >
                      {t('area')}
                    </Typography>
                    <Typography
                      sx={{
                        textAlign: 'left',
                        display: '-webkit-box',
                        WebkitLineClamp: '1',
                        lineClamp: '1',
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                      }}
                    >
                      {areas?.find((x) => x?.id === selectedAreaId)?.areaName}
                    </Typography>
                  </>
                ) : (
                  <Typography>{t('area')}</Typography>
                )}
              </AccordionSummary>
              <AccordionDetails>
                <ManageLayoutItem
                  loading={loading}
                  disabled={selectedPlantId > 0 ? false : true}
                  data={availableAreas}
                  dataType="area"
                  addLayoutItem={handleOpenDialogAddLayoutItem}
                  selectedData={selectedAreaId}
                  handleChangeData={handleChangeSelectedArea}
                  changeEnabled={changeEnabled}
                />
              </AccordionDetails>
            </Accordion>
            <Accordion
              expanded={expandedAccordion === 'line'}
              onChange={handleChangeAccordion('line')}
              disabled={loading || (selectedAreaId > 0 ? false : true)}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel-line-content"
                id="panel-line-header"
              >
                {selectedLineId > 0 ? (
                  <>
                    <Typography
                      sx={{ width: '150px', flexShrink: 0, textAlign: 'left' }}
                    >
                      {t('line')}
                    </Typography>
                    <Typography
                      sx={{
                        textAlign: 'left',
                        display: '-webkit-box',
                        WebkitLineClamp: '1',
                        lineClamp: '1',
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                      }}
                    >
                      {lines?.find((x) => x?.id === selectedLineId)?.lineName}
                    </Typography>
                  </>
                ) : (
                  <Typography>{t('line')}</Typography>
                )}
              </AccordionSummary>
              <AccordionDetails>
                <ManageLayoutItem
                  loading={loading}
                  disabled={selectedAreaId > 0 ? false : true}
                  data={availableLines}
                  dataType="line"
                  addLayoutItem={handleOpenDialogAddLayoutItem}
                  selectedData={selectedLineId}
                  handleChangeData={handleChangeSelectedLine}
                  changeEnabled={changeEnabled}
                />
              </AccordionDetails>
            </Accordion>
            <Accordion
              expanded={expandedAccordion === 'equipment'}
              onChange={handleChangeAccordion('equipment')}
              disabled={loading || (selectedLineId > 0 ? false : true)}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel-equipment-content"
                id="panel-equipment-header"
              >
                {selectedEquipmentId > 0 ? (
                  <>
                    <Typography
                      sx={{ width: '150px', flexShrink: 0, textAlign: 'left' }}
                    >
                      {t('equipment')}
                    </Typography>
                    <Typography
                      sx={{
                        textAlign: 'left',
                        display: '-webkit-box',
                        WebkitLineClamp: '1',
                        lineClamp: '1',
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        wordBreak: 'break-word',
                      }}
                    >
                      {
                        equipments?.find((x) => x?.id === selectedEquipmentId)
                          ?.equipmentName
                      }
                    </Typography>
                  </>
                ) : (
                  <Typography>{t('equipment')}</Typography>
                )}
              </AccordionSummary>
              <AccordionDetails>
                <ManageLayoutItem
                  loading={loading}
                  disabled={selectedLineId > 0 ? false : true}
                  data={availableEquipments}
                  dataType="equipment"
                  addLayoutItem={handleOpenDialogAddLayoutItem}
                  selectedData={selectedEquipmentId}
                  handleChangeData={handleChangeSelectedEquipment}
                  changeEnabled={changeEnabled}
                />
              </AccordionDetails>
            </Accordion>
          </Paper>
        </Container>
      ) : null}
      <DialogAddLayoutItem
        open={isOpenDialogAddLayoutItem}
        onClose={handleCloseDialogAddLayoutItem}
        onValidate={handleValidateDialogAddLayoutItem}
        type={dialogAddLayoutItemType}
        id={dialogAddLayoutItemId}
      />
    </Container>
  );
}

export default ManageLayout;
